import {
  GetSceneTimelineByIdDocument,
  UpdateOneEventTimelineMutation,
  UpdateOneEventTimelineMutationVariables,
} from '@src/components/generated/graphql';
import * as Apollo from '@apollo/client';
import { NODES_TYPES, QUEST_TYPES } from '../timeline-node-manager/types';
import { ConditionType } from '@src/components/generated/graphql';
import type { TimelineType } from '@src/types/event-timeline';
import { v4 as uuidv4 } from 'uuid';

function ensureUniqueIds(nodes: any[]): any[] {
  const idMap = new Map();
  const usedIds = new Set();

  return nodes.map((node) => {
    if (!node?.id) {
      const newId = uuidv4();
      return { ...node, id: newId };
    }

    if (usedIds.has(node.id)) {
      const newId = uuidv4();
      idMap.set(node.id, newId);
      usedIds.add(newId);
      return { ...node, id: newId };
    }

    usedIds.add(node.id);
    return node;
  });
}

// Produces Data structure for updating timeline as a tree.
// Unconnected Nodes will be trigger error for user and cancel save.
// TODO: We need to optimize this method and do not bound to specific Nodes Types.
export function buildTimelineDataByNodeState(nds: any[], edges: any[]) {
  if (!Array.isArray(nds)) {
    nds = [];
  }
  if (!Array.isArray(edges)) {
    edges = [];
  }

  const nodeIds = new Set<string>();
  const duplicateIds: string[] = [];

  nds.forEach((node: any) => {
    if (node?.id) {
      if (nodeIds.has(node.id)) {
        duplicateIds.push(node.id);
      } else {
        nodeIds.add(node.id);
      }
    }
  });

  if (duplicateIds.length > 0) {
    console.warn(
      `Duplicate node IDs found: ${duplicateIds.join(
        ', '
      )}. Generating new IDs.`
    );
    nds = ensureUniqueIds(nds);
  }

  const timelineData: TimelineType = { id: '', events: [] };
  let nodes = [...nds];

  nodes.forEach((node) => {
    if (node?.type === NODES_TYPES.EVENT || node?.type === NODES_TYPES.QUEST) {
      const questType =
        node.type === NODES_TYPES.QUEST
          ? node.data?.questType || QUEST_TYPES.SEQUENTIAL
          : undefined;
      timelineData.events?.push({
        ...node.data,
        id: node.id,
        isQuest: node.type === NODES_TYPES.QUEST,
        questType: questType,
        actions: [],
        conditions: [],
      });
      nodes = nodes.filter((n) => n.id !== node.id);
    }
  });

  // Find Actions and Conditions
  edges.forEach((edge) => {
    if (!edge?.source || !edge?.target) {
      return;
    }

    const targetNode = nodes.find((node) => node?.id === edge.target);
    if (!targetNode) {
      return;
    }

    if (targetNode.type === NODES_TYPES.ACTION) {
      let targetEvent: any = null;

      targetEvent = timelineData.events?.find(
        (event: any) => event.id === edge.source
      );

      if (
        !targetEvent &&
        targetNode.data?.isOnComplete &&
        targetNode.data?.onCompleteConditionId
      ) {
        targetEvent = timelineData.events?.find((event: any) =>
          event.conditions?.some(
            (condition: any) =>
              condition.id === targetNode.data?.onCompleteConditionId
          )
        );
      }

      if (targetEvent) {
        if (!targetEvent.actions) {
          targetEvent.actions = [];
        }
        targetEvent.actions.push({ ...targetNode.data, id: targetNode.id });
        nodes = nodes.filter((n) => n.id !== targetNode.id);
      }
    }

    if (
      targetNode.type === NODES_TYPES.CONDITION ||
      targetNode?.type === NODES_TYPES.COLLECTIBLE ||
      targetNode?.type === NODES_TYPES.DESTROYABLE
    ) {
      timelineData.events?.forEach((event) => {
        if (event.id === edge.source) {
          if (!event.conditions) {
            event.conditions = [];
          }
          event.conditions.push({
            ...targetNode.data,
            id: targetNode.id,
            params: {
              ...targetNode.data.params,
              title: targetNode.data.params?.title || '',
              description: targetNode.data.params?.description || '',
            },
          });
          nodes = nodes.filter((n) => n.id !== targetNode.id);
        }
      });
    }
  });
  // Find all Targets
  edges.forEach((edge) => {
    const targetNode = nodes.find((node) => node.id === edge.target);
    if (targetNode?.type === NODES_TYPES.TARGET) {
      timelineData.events?.forEach((event) => {
        event.actions?.forEach((action) => {
          if (action.id === edge.source) {
            action.target = { ...targetNode.data, id: targetNode.id };
            nodes = nodes.filter((n) => n.id !== targetNode.id);
          }
        });
      });
    }
  });
  // Find Event Links
  nodes.forEach((node) => {
    if (node.type === NODES_TYPES.EVENT_LINK) {
      const sourceNode = node.data.sourceEvent;
      const targetNode = node.data.targetEvent;
      if (!sourceNode || !targetNode) {
        return;
      }

      timelineData.events?.forEach((event) => {
        if (event.id === targetNode) {
          const condition: any = {
            id: node.id,
            type: ConditionType.EventCompleted,
            params: {
              eventId: sourceNode,
            },
          };
          if (!event.conditions) {
            event.conditions = [];
          }
          event.conditions.push(condition);
        }
      });

      nodes = nodes.filter((n) => n.id !== node.id);
    }
  });

  if (nodes.length) {
    throw new Error(
      'You have unused nodes in constructor, please remove them before saving changes.'
    );
  }

  return timelineData;
}

export function updateTimelineInput(
  nodes: any[],
  edges: any[],
  timeline: TimelineType
) {
  const timeLineData = buildTimelineDataByNodeState(nodes, edges);

  if (!timeline?.id) {
    throw new Error('Timeline ID is required');
  }

  timeLineData.id = timeline.id;
  timeLineData.name = timeline.name || 'Timeline';

  const result = buildUpdateTimelineInput(nodes, edges, timeLineData);
  return result;
}

function buildUpdateTimelineInput(
  nodes: any[],
  edges: any[],
  timeline: TimelineType
): Apollo.MutationHookOptions<
  UpdateOneEventTimelineMutation,
  UpdateOneEventTimelineMutationVariables
> {
  const buildTargetUpserts = (target: any) => ({
    where: { id: { equals: target.id } },
    update: {
      type: { set: target?.type ?? null },
      objectId: { set: target?.objectId ?? null },
    },
    create: {
      id: target.id,
      type: target?.type,
      objectId: target?.objectId,
    },
  });

  const buildActionUpserts = (actions: any[]) =>
    actions?.map((action: any) => ({
      where: { id: action.id },
      update: {
        delay: { set: action.delay ?? null },
        duration: { set: action.duration ?? null },
        params: action.params ?? null,
        type: { set: action.type ?? null },
        isOnComplete: { set: action.isOnComplete ?? null },
        onCompleteConditionId: { set: action.onCompleteConditionId ?? null },
        target: action.target
          ? {
              upsert: buildTargetUpserts(action.target),
            }
          : undefined,
      },
      create: {
        id: action.id,
        delay: action.delay,
        duration: action.duration,
        isOnComplete: action.isOnComplete,
        onCompleteConditionId: action.onCompleteConditionId,
        params: action.params ?? null,
        type: action.type,
        target: action.target
          ? {
              connectOrCreate: {
                where: { id: action.target.id },
                create: {
                  id: action.target.id,
                  type: action.target.type,
                  objectId: action.target.objectId,
                },
              },
            }
          : undefined,
      },
    }));

  const buildConditionUpserts = (conditions: any[]) =>
    conditions?.map((condition: any) => ({
      where: { id: condition.id },
      update: {
        type: { set: condition.type ?? null },
        params: condition.params ?? null,
        operator: { set: condition.operator ?? null },
      },
      create: {
        id: condition.id,
        type: condition.type,
        params: condition.params ?? null,
        operator: condition.operator ?? null,
      },
    }));

  const buildEventInputs = (event: any) => {
    if (!event.id) {
      console.error('Event missing ID:', event);
      throw new Error('Event missing ID');
    }

    if (event.actions) {
      const actionIds = new Set<string>();
      const duplicateActionIds: string[] = [];
      event.actions.forEach((action: any) => {
        if (action.id) {
          if (actionIds.has(action.id)) {
            duplicateActionIds.push(action.id);
          } else {
            actionIds.add(action.id);
          }
        }
      });

      if (duplicateActionIds.length > 0) {
        console.warn(
          `Duplicate action IDs in event ${event.id}:`,
          duplicateActionIds
        );
      }
    }

    if (event.conditions) {
      const conditionIds = new Set<string>();
      const duplicateConditionIds: string[] = [];
      event.conditions.forEach((condition: any) => {
        if (condition.id) {
          if (conditionIds.has(condition.id)) {
            duplicateConditionIds.push(condition.id);
          } else {
            conditionIds.add(condition.id);
          }
        }
      });

      if (duplicateConditionIds.length > 0) {
        console.warn(
          `Duplicate condition IDs in event ${event.id}:`,
          duplicateConditionIds
        );
      }
    }

    return {
      where: { id: event.id },
      create: {
        id: event.id,
        name: event.name ?? '',
        status: event.status,
        isQuest: event.isQuest,
        questType: event.questType,
        conditions: event?.conditions?.length
          ? {
              createMany: {
                data: event.conditions?.map((condition: any) => ({
                  id: condition.id,
                  type: condition.type,
                  params: condition.params ?? null,
                  operator: condition.operator ?? null,
                })),
              },
            }
          : undefined,
        actions: event?.actions?.length
          ? {
              create: event.actions?.map((action: any) => ({
                id: action.id,
                delay: action.delay,
                duration: action.duration,
                params: action.params ?? null,
                type: action.type,
                target: action.target
                  ? {
                      connectOrCreate: {
                        where: { id: action.target.id },
                        create: {
                          id: action.target.id,
                          type: action.target.type,
                          objectId: action.target.objectId,
                        },
                      },
                    }
                  : undefined,
              })),
            }
          : undefined,
      },
      update: {
        name: { set: event.name ?? null },
        isQuest: { set: event.isQuest ?? false },
        questType: { set: event.questType ?? null },
        status: event.status ? { set: event.status ?? null } : undefined,
        conditions: event.conditions?.length
          ? {
              upsert: buildConditionUpserts(event.conditions),
            }
          : undefined,
        actions: event.actions?.length
          ? {
              upsert: buildActionUpserts(event.actions),
            }
          : undefined,
      },
    };
  };

  return {
    variables: {
      where: {
        id: timeline.id,
      },
      data: {
        name: { set: timeline.name ?? null },
        events: timeline?.events?.length
          ? {
              upsert: timeline?.events?.map(buildEventInputs),
            }
          : undefined,
        nodesConfig: {
          update: {
            where: {
              timelineId: {
                equals: timeline?.id,
              },
            },
            data: {
              nodes,
              edges,
            },
          },
        },
      },
    },
    refetchQueries: [GetSceneTimelineByIdDocument],
  };
}
